import React, { useState, useEffect, useCallback } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DoshaChart } from './DoshaChart';
import { constitutionalRecommendations } from '../../data/recommendations';
import { getAssessmentHistory } from '../../utils/storage';

export const ResultsDisplay: React.FC = () => {
  const { state, navigateToScreen } = useAssessment();
  const [history, setHistory] = useState<any[]>([]);

  const loadAssessmentHistory = useCallback(() => {
    try {
      const history = getAssessmentHistory();
      setHistory(history);
    } catch (error) {
      console.error('Error loading assessment history:', error);
    }
  }, []);

  useEffect(() => {
    loadAssessmentHistory();
  }, [loadAssessmentHistory]);

  useEffect(() => {
    if (state.currentResult && state.userEmail) {
      // Save results to history
      const history = getAssessmentHistory();
      const newEntry = {
        id: Date.now(),
        date: new Date().toISOString(),
        email: state.userEmail,
        result: state.currentResult
      };
      
      const updatedHistory = [newEntry, ...history];
      localStorage.setItem('assessmentHistory', JSON.stringify(updatedHistory));
      setHistory(updatedHistory);
    }
  }, [state.currentResult, state.userEmail, loadAssessmentHistory]);

  if (!state.currentResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Results Available</h2>
          <p className="text-gray-600 mb-6">Please complete the assessment first.</p>
          <Button onClick={() => navigateToScreen('welcome')}>
            Take Assessment
          </Button>
        </div>
      </div>
    );
  }

  const { bodyType, percentages, description } = state.currentResult;

  // Get specific recommendations from the comprehensive data
  const getSpecificRecommendations = (bodyType: string) => {
    return constitutionalRecommendations[bodyType] || constitutionalRecommendations['vata'];
  };

  const specificRecommendations = getSpecificRecommendations(bodyType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-amber-200 mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm font-medium text-green-700">Assessment Complete</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-3">
            Your <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
              {bodyType.charAt(0).toUpperCase() + bodyType.slice(1)}
            </span> Constitution
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {/* Tridoshic Recommendations - Highlighted Above Dosha Balance */}
        {bodyType === 'tridoshic' && (
          <Card className="bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 border-0 shadow-xl p-6 mb-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Special Focus: Tridoshic Constitution</h3>
              <p className="text-lg text-gray-600">Your rare and harmonious balance requires special attention to maintain equilibrium</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-purple-200">
                <h4 className="text-xl font-bold text-purple-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Dietary Guidelines
                </h4>
                
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <h5 className="text-lg font-semibold text-emerald-800 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Foods to Favor
                    </h5>
                    <ul className="space-y-2">
                      {specificRecommendations.dietary.foodsToFavor.map((food, index) => (
                        <li key={index} className="flex items-start text-emerald-700">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm">{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h5 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Foods to Avoid
                    </h5>
                    <ul className="space-y-2">
                      {specificRecommendations.dietary.foodsToAvoid.map((food, index) => (
                        <li key={index} className="flex items-start text-red-700">
                          <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm">{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-5 border border-pink-200">
                <h4 className="text-xl font-bold text-pink-800 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-2 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Lifestyle Guidelines
                </h4>
                
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h5 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Exercise & Movement
                    </h5>
                    <ul className="space-y-2">
                      {specificRecommendations.lifestyle.exercise.map((exercise, index) => (
                        <li key={index} className="flex items-start text-purple-700">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm">{exercise}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <h5 className="text-lg font-semibold text-orange-800 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Daily Routine
                    </h5>
                    <ul className="space-y-2">
                      {specificRecommendations.lifestyle.dailyRoutine.map((routine, index) => (
                        <li key={index} className="flex items-start text-orange-700">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm">{routine}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Main Results Grid - Only Dosha Balance */}
        <div className="mb-8">
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Your Dosha Balance</h3>
            <DoshaChart percentages={percentages} />
          </Card>
        </div>

        {/* Specific Recommendations from FINAL.md */}
        {bodyType !== 'tridoshic' && (
          <Card className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 border-0 shadow-xl p-8 mb-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Your Personalized Recommendations</h3>
              <p className="text-lg text-gray-600">Tailored guidance for your {bodyType.charAt(0).toUpperCase() + bodyType.slice(1)} constitution</p>
            </div>
            
            {/* Dietary Guidelines */}
            <div className="mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-green-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-gray-800">Dietary Guidelines</h4>
              </div>
              
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-emerald-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-emerald-800">Foods to Favor</h5>
                  </div>
                  <div className="space-y-3">
                    {specificRecommendations.dietary.foodsToFavor.map((food, index) => (
                      <div key={index} className="flex items-start group">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-emerald-500 transition-colors"></div>
                        <span className="text-sm text-emerald-700 group-hover:text-emerald-800 transition-colors">{food}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-red-800">Foods to Avoid</h5>
                  </div>
                  <div className="space-y-3">
                    {specificRecommendations.dietary.foodsToAvoid.map((food, index) => (
                      <div key={index} className="flex items-start group">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-red-500 transition-colors"></div>
                        <span className="text-sm text-red-700 group-hover:text-red-800 transition-colors">{food}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-blue-800">Meal Timing & Habits</h5>
                  </div>
                  <div className="space-y-3">
                    {specificRecommendations.dietary.mealTiming.map((timing, index) => (
                      <div key={index} className="flex items-start group">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-blue-500 transition-colors"></div>
                        <span className="text-sm text-blue-700 group-hover:text-blue-800 transition-colors">{timing}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Lifestyle Guidelines */}
            <div>
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-gray-800">Lifestyle Guidelines</h4>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-purple-800">Exercise & Movement</h5>
                  </div>
                  <div className="space-y-3">
                    {specificRecommendations.lifestyle.exercise.map((exercise, index) => (
                      <div key={index} className="flex items-start group">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-purple-500 transition-colors"></div>
                        <span className="text-sm text-purple-700 group-hover:text-purple-800 transition-colors">{exercise}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-indigo-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <h5 className="text-lg font-semibold text-indigo-800">Daily Routine</h5>
                  </div>
                  <div className="space-y-3">
                    {specificRecommendations.lifestyle.dailyRoutine.map((routine, index) => (
                      <div key={index} className="flex items-start group">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-indigo-500 transition-colors"></div>
                        <span className="text-sm text-indigo-700 group-hover:text-indigo-800 transition-colors">{routine}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Book References */}
        <Card className="bg-gradient-to-br from-gray-50 to-neutral-50 border-0 shadow-xl mb-8">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Based on Ancient Wisdom</h3>
            <p className="text-gray-600 text-sm">These recommendations are derived from classical Ayurvedic texts</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <h4 className="font-medium text-gray-800 mb-1">Charaka Samhita</h4>
              <p className="text-xs text-gray-500">Foundation of Ayurvedic medicine (400-200 BCE)</p>
            </div>
            <div className="text-center">
              <h4 className="font-medium text-gray-800 mb-1">Ashtanga Hridayam</h4>
              <p className="text-xs text-gray-500">Comprehensive guide to Ayurvedic principles</p>
            </div>
            <div className="text-center">
              <h4 className="font-medium text-gray-800 mb-1">Sushruta Samhita</h4>
              <p className="text-xs text-gray-500">Ancient surgical and medical treatise</p>
            </div>
          </div>
        </Card>

        {/* Buy Me a Coffee Section */}
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-0 shadow-xl mb-8">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Support Ancient Wisdom</h3>
            <p className="text-gray-600 mb-4 max-w-2xl mx-auto">
              This comprehensive Ayurvedic assessment is based on 5,000 years of ancient wisdom. 
              If you found this content helpful, consider sponsoring a coffee to support our work.
            </p>
            
            <div className="bg-white rounded-lg p-4 border-2 border-dashed border-orange-300 mb-4">
              <div className="text-sm text-gray-600 mb-2">If you like this content, sponsor a coffee:</div>
              <a 
                href="https://buymeacoffee.com/dhruvaa" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.205 4.791a5.938 5.938 0 0 0-4.209-1.754A5.906 5.906 0 0 0 12 4.595a5.904 5.904 0 0 0-3.996-1.558 5.942 5.942 0 0 0-4.213 1.758c-2.353 2.363-2.352 6.059.002 8.412L12 21.414l8.207-8.207c2.354-2.353 2.355-6.049-.002-8.416z"/>
                </svg>
                Buy Me a Coffee
              </a>
            </div>
            
            <div className="grid md:grid-cols-3 gap-3 text-sm text-gray-600 mb-4">
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>100% Free Assessment</span>
              </div>
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Ancient Wisdom Preserved</span>
              </div>
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Continuous Improvements</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Didn't like the content?</strong> Contact us at{' '}
                <a href="mailto:support@futurezing.com" className="text-blue-600 hover:text-blue-800 underline">
                  support@futurezing.com
                </a>
                {' '}for complaints or feedback.
              </p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
          <Button
            onClick={() => navigateToScreen('welcome')}
            className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          >
            Take Assessment Again
          </Button>
        </div>

        {/* History Preview */}
        {history.length > 0 && (
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Assessments</h3>
            </div>
            <div className="space-y-3">
              {history.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800 capitalize">{item.result.bodyType}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(item.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      V: {item.result.percentages.vata}% | P: {item.result.percentages.pitta}% | K: {item.result.percentages.kapha}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}